import angular from 'angular';
import { flow } from 'lodash';

import { consume, Stream2Async, shareIterator, anim } from '../asyncutils';

/**
 * The following constant defines how many mousemoves have to occur
 * after a mousedown in order to initiate the drag
 * Otherwise it is to eager and easy to mistakenly start dragging
 * when the intention was to click
 */
const DRAG_DELAY = 5;

function _clearUserSelection() {
  if (window.getSelection) {
    if (window.getSelection().empty) {
      // Chrome
      window.getSelection().empty();
    } else if (window.getSelection().removeAllRanges) {
      // Firefox
      window.getSelection().removeAllRanges();
    }
  } else if (document.selection) {
    // IE?
    document.selection.empty();
  }
}

/* eslint-disable no-continue */
export default function draggableShift(Utils, WindowScroll, $timeout, $document, $window) {
  'ngInject';

  const $body = angular.element($document[0].body);
  let _currentDropSet;
  let _currentDropTarget;

  let stream = startStream();

  $document.on('mousemove', (event) => {
    stream.stream2async.collect({ event });
    if (stream.stream2async.queue.length > 200) {
      /**
       * Sometimes for unknown reasons the stream get clogged
       * so just stop it and start a new one.
       */
      console.warn('RESTARTING STREAM2ACYNC');
      stream.stream2async.queue.forEach((queueItem) => {
        if (queueItem.event?.type === 'abort') {
          queueItem.setDragging(false);
        }
      });
      stream.stop();
      stream = startStream();
    }
  });

  $document.on('mouseup', (event) => stream.stream2async.collect({ event }));
  angular.element($window).on('scroll', (event) => stream.stream2async.collect({ event }));

  function startStream() {
    const stream2async = new Stream2Async();
    let stop;
    const stopPromise = new Promise((resolve) => (stop = resolve));
    const pipeline = flow([
      handleMousedown.bind(this),
      handleMousemove.bind(this),
      handleDropHighlight.bind(this),
      handleAbort.bind(this),
      handleMouseup.bind(this),
    ]);

    consume(pipeline(stream2async.produce(stopPromise)));

    return { stream2async, stop };
  }

  async function* handleMousedown(iterable) {
    function setIsCopy(item, $clones, $element) {
      if (item.event.shiftKey || item.event.ctrlKey || item.event.metaKey || item.event.altKey) {
        item.isCopy = true;
        $clones.addClass('shift-key-drag');
        $element.addClass('shift-key-drag');
      } else {
        item.isCopy = false;
      }
    }

    async function waitForNextItems(shared) {
      const nextItems = [];
      for await (const next of shared) {
        nextItems.push(next);
        if (nextItems.length >= DRAG_DELAY || next.event.type !== 'mousemove') {
          break;
        }
      }

      return nextItems;
    }

    function _adjustTopLeft($clones, initElemRect, actualInitRect) {
      if (initElemRect.top !== actualInitRect.top || initElemRect.left !== actualInitRect.left) {
        initElemRect.top -= actualInitRect.top - initElemRect.top;
        initElemRect.left -= actualInitRect.left - initElemRect.left;

        $clones.css({
          top: `${initElemRect.top}px`,
          left: `${initElemRect.left}px`,
        });
      }
    }

    const shared = shareIterator(iterable);
    for await (const item of shared) {
      const { event, $element, setDragging } = item;
      if (event.type === 'mousedown') {
        const nextItems = await waitForNextItems(shared);
        if (nextItems[nextItems.length - 1].event.type !== 'mousemove') {
          /**
           * If after the mousedown there weren't <DRAG_DELAY>
           * mousemoves just continue awaiting - don't initiate the drag,
           * just wait for another mousedown
           */
          continue;
        }

        setDragging(true);
        $element.addClass('draggable-shift-dragging');

        const initMousePos = {
          x: event.clientX + WindowScroll.x,
          y: event.clientY + WindowScroll.y,
        };

        const initElemRect = Utils.extendWithProps($element[0].getBoundingClientRect(), {}, [
          'top',
          'left',
          'width',
          'height',
        ]);
        const $helper = $element.clone();
        const $ghost = $element.clone();
        const $clones = angular.element().add($helper).add($ghost);

        $clones.css({
          position: 'fixed',
          top: `${initElemRect.top}px`,
          left: `${initElemRect.left}px`,
          height: `${initElemRect.height}px`,
          width: `${initElemRect.width}px`,
        });

        setIsCopy(item, $clones, $element);

        $body.addClass('user-select-none');
        $helper.addClass('draggable-shift-clone');
        $ghost.addClass('draggable-shift-ghost');
        $element.parent().append($clones);

        const actualInitRect = $clones[0].getBoundingClientRect();
        _adjustTopLeft($clones, initElemRect, actualInitRect);

        const postDragCleanup = () => {
          $clones.remove();
          $element.removeClass('draggable-shift-dragging');
          $element.removeClass('shift-key-drag');
          _clearUserSelection();
          $body.removeClass('user-select-none');
        };

        const [initDropSet] = await getDropTargets({ $ghost, $clones, ...item });
        const maxDropSpan = initDropSet.size || 1;

        const itemExtensions = {
          initMousePos,
          initElemRect,
          $helper,
          $ghost,
          $clones,
          maxDropSpan,
          initDropSet,
          postDragCleanup,
        };

        while (nextItems.length) {
          yield {
            ...itemExtensions,
            ...item,
            ...nextItems.shift(),
          };
        }

        for await (const nested of shared) {
          yield {
            ...itemExtensions,
            ...item,
            ...nested,
          };

          /**
           * This inner loop iterates over the same iterator that the outer loop
           * consuming mousemoves during the drag operation. It passes to the next steps
           * of the pipeline:
           * - all the flags and objects calculated at drag-start i.e. $helper, $ghost etc.
           * - all the objects passed from the directive link function from
           *   the mousedown listener i.e. setDragging, $element
           * - the current DOM event (mousemove or mouseup) data
           *
           * When finally a mouseup occurs it got yielded to the rest to the pipeline
           * but now we can break out of the inner loop and listen for another mousedown
           * in the outer loop
           */

          if (nested.event.type === 'mouseup' || nested.event.type === 'abort') {
            break;
          }
        }

        continue;
      }

      /**
       * Otherwise do nothing, do not even yield anything,
       * just await for a new item from the iterator.
       * Thanks to that the "item" gets eaten here
       * and doesn't make it's way to the rest of the pipeline.
       *
       * There is an inner loop that yields other event types
       * that follow a mousedown that are relevant for the drag operation.
       */
    }
  }

  async function* handleMousemove(iterable) {
    function drag(item) {
      const { x, y } = { x: item.event.clientX, y: item.event.clientY };

      const { $helper, $clones, initElemRect, initMousePos } = item;
      if (!$helper.hasClass('dragging')) {
        $timeout(
          () => {
            $helper.addClass('dragging');
          },
          0,
          false
        );
      }

      const offsetX = item.draggableOffsetX || 0;

      $clones.css({
        top: `${initElemRect.top + y - (initMousePos.y - WindowScroll.y)}px`,
        left: `${initElemRect.left + x - (initMousePos.x - WindowScroll.x) + offsetX}px`,
      });
    }

    for await (const item of iterable) {
      if (item.event.type === 'mousemove') {
        drag(item);
      }

      yield item;
    }
  }

  async function* handleDropHighlight(iterable) {
    let lastSet = new Set();
    let lastEvent = null;

    function removeHighlights(keepSet = new Set()) {
      const elems = [];
      for (const e of lastSet) {
        if (!keepSet.has(e)) {
          // to not remove classes just to add them again immediately
          elems.push(e);
        }
      }

      angular.element(elems).removeClass('draggable-shift-drop-highlight');
    }

    async function addHighlights(item) {
      const [set, target] = await getDropTargets(item);
      _currentDropSet = set;
      _currentDropTarget = target;

      removeHighlights(set);
      const elems = [];
      for (const e of set) {
        if (!lastSet.has(e)) {
          // no need to add classes to elements that already have them
          elems.push(e);
        }
      }

      angular.element(elems).addClass('draggable-shift-drop-highlight');
      lastSet = set;
    }

    async function addRemoveHighlights(item) {
      if (item.event.type === 'mousemove') {
        await addHighlights(item);

        if (lastEvent === 'mousemove') {
          return;
        }
      }

      removeHighlights();
      lastSet = new Set();
    }

    for await (const item of iterable) {
      lastEvent = item.event.type;
      addRemoveHighlights(item);
      yield item;
    }
  }

  async function* handleAbort(iterable) {
    for await (const item of iterable) {
      if (item.event.type === 'abort') {
        /**
         * Cleanup and don't yield.
         */
        const { postDragCleanup } = item;
        item.setDragging(false);
        postDragCleanup();
        continue;
      }

      yield item;
    }
  }

  async function* handleMouseup(iterable) {
    async function didDropSuccessfully({ dropTarget, ...args }) {
      if (dropTarget) {
        try {
          return (await dropTarget.__dsDrop(args)) !== false;
        } catch {
          return false;
        }
      }

      return false;
    }

    for await (const item of iterable) {
      if (item.event.type === 'mouseup') {
        const {
          initElemRect,
          initMousePos,
          initDropSet,
          $helper,
          $clones,
          $element,
          event,
          setDragging,
          isCopy,
          postDragCleanup,
        } = item;

        await $timeout(0, false);
        const successfullyDropped = await didDropSuccessfully({
          dropTarget: _currentDropTarget,
          dragEl: $element,
          isCopy,
          initDropSet,
          targetDropSet: _currentDropSet,
        });

        $helper.removeClass('dragging');

        if (!successfullyDropped) {
          const dropX = event.clientX;
          const dropY = event.clientY;

          for await (const k of anim(500, { easing: 'cubicOut', skipInitial: true })) {
            /**
             * The following are the equations of a quarter of a circle
             * x^2 + y^2 = 1
             * y = 1 - sqrt(1 - x^2)
             */
            const x = 1 - k;
            const y = 1 - (1 - x ** 2) ** 0.5;

            $clones.css({
              top: `${initElemRect.top + y * (dropY - (initMousePos.y - WindowScroll.y))}px`,
              left: `${initElemRect.left + x * (dropX - (initMousePos.x - WindowScroll.x))}px`,
            });
          }
        }

        setDragging(false);
        postDragCleanup();
      }

      yield item;
    }
  }

  async function getDropTargets({ $clones, $element, $ghost, maxDropSpan = Infinity }) {
    const QUANT = 5;
    const margin = maxDropSpan === Infinity ? QUANT : 0;

    const $all = $clones.add($element);
    $all.addClass('pointer-events-none');

    /**
     * .getBoundingClientRect() forces a reflow in the browser causing
     * some 'lagging' during the animation when it's called as often as
     * per every mousemove. Using the following trick we are reading the
     * boundingClientRect in an animation frame.
     *
     * In live app we wanna use __Promise (original promise constructor)
     * instead of monkeypatched Promise <=> $q. So that we don't trigger
     * unnecessary digests - think performance 💪
     */

    const Promise = __TEST__ ? global.Promise : __Promise;
    const result = await new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        const ghostRect = $ghost[0].getBoundingClientRect();
        let dir = 1;
        const y = ghostRect.top + ghostRect.height / 2;
        const set = new Set();
        let first = null;

        for (
          let x = ghostRect.left + ghostRect.width / 2, step = 1;
          x < ghostRect.left + ghostRect.width - margin &&
          x > ghostRect.left + margin &&
          set.size < maxDropSpan;
          x += dir * step * QUANT, dir *= -1, step++
        ) {
          const fromPoint = document.elementFromPoint(x, y);
          if (fromPoint?.getAttribute('draggable-shift-drop-target') === 'true') {
            set.add(fromPoint);

            if (!first) {
              first = fromPoint;
            }
          }
        }

        resolve([set, first]);
      });
    });

    $all.removeClass('pointer-events-none');

    return result;
  }

  function link($scope, $element, $attrs, $isDraggingCtrl) {
    const setDragging = (val) => $isDraggingCtrl?.setDragging(val);

    $scope.$on('$destroy', () => {
      const event = {
        type: 'abort',
      };

      stream.stream2async.collect({ $element, event, setDragging });
    });

    $element.on('mousedown', (e) => {
      if ($scope.$eval($attrs.draggableShift) && e.button === 0) {
        stream.stream2async.collect({
          $element,
          setDragging,
          event: e,
          draggableOffsetX: $scope.$eval($attrs.draggableOffsetX),
        });
      }
    });

    $element.on('dragstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });
  }

  const directive = {
    require: '^?draggableShiftIsDragging',
    link,
  };

  if (__TEST__) {
    directive[Symbol.testData] = {
      stream2async: stream.stream2async,
      handleMousedown,
      handleMousemove,
      handleDropHighlight,
      handleAbort,
      handleMouseup,
      getDropTargets,
      DRAG_DELAY,
    };
  }

  return directive;
}
